const safeLinkPattern = /^(?:https?:|mailto:|tel:)/i;
const richTextWhitespace = String.raw`(?:\s|&nbsp;|&#160;|&#xa0;)`;
const richTextWhitespaceOnlyPattern = new RegExp(
  `^${richTextWhitespace}*$`,
  'i',
);
const richTextLeadingWhitespacePattern = new RegExp(
  `^${richTextWhitespace}+`,
  'i',
);
const richTextTrailingWhitespacePattern = new RegExp(
  `${richTextWhitespace}+$`,
  'i',
);

export const RSVP_NOTE_MAX_LENGTH = 2000;

export function isRsvpNoteWithinLengthLimit(notes: string) {
  return Array.from(notes).length <= RSVP_NOTE_MAX_LENGTH;
}

export function trimRichText(html: string) {
  const tokens = html.trim().split(/(<[^>]*>)/g);
  const firstTextIndex = tokens.findIndex(
    (token) =>
      !token.startsWith('<') && !richTextWhitespaceOnlyPattern.test(token),
  );

  if (firstTextIndex === -1) {
    return html.trim();
  }

  const lastTextIndex = tokens.findLastIndex(
    (token) =>
      !token.startsWith('<') && !richTextWhitespaceOnlyPattern.test(token),
  );

  for (let tokenIndex = 0; tokenIndex < firstTextIndex; tokenIndex += 1) {
    if (!tokens[tokenIndex]?.startsWith('<')) {
      tokens[tokenIndex] = '';
    }
  }
  for (
    let tokenIndex = lastTextIndex + 1;
    tokenIndex < tokens.length;
    tokenIndex += 1
  ) {
    if (!tokens[tokenIndex]?.startsWith('<')) {
      tokens[tokenIndex] = '';
    }
  }

  tokens[firstTextIndex] = tokens[firstTextIndex]?.replace(
    richTextLeadingWhitespacePattern,
    '',
  );
  tokens[lastTextIndex] = tokens[lastTextIndex]?.replace(
    richTextTrailingWhitespacePattern,
    '',
  );

  return tokens.join('');
}

export const richTextSystemFontFamily =
  process.env.EXPO_OS === 'web'
    ? 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    : process.env.EXPO_OS === 'android'
      ? 'sans-serif'
      : 'System';

function decodeHtmlEntity(entity: string) {
  switch (entity.toLowerCase()) {
    case '&amp;':
      return '&';
    case '&apos;':
    case '&#39;':
      return "'";
    case '&gt;':
      return '>';
    case '&lt;':
      return '<';
    case '&nbsp;':
    case '&#160;':
    case '&#xa0;':
      return ' ';
    case '&quot;':
      return '"';
    default:
      break;
  }

  const decimalMatch = /^&#(\d+);$/i.exec(entity);
  const hexadecimalMatch = /^&#x([\da-f]+);$/i.exec(entity);
  const codePoint = decimalMatch
    ? Number.parseInt(decimalMatch[1] ?? '', 10)
    : hexadecimalMatch
      ? Number.parseInt(hexadecimalMatch[1] ?? '', 16)
      : Number.NaN;

  return Number.isFinite(codePoint) && codePoint > 0 && codePoint <= 0x10ffff
    ? String.fromCodePoint(codePoint)
    : entity;
}

export function isSafeRichTextLink(url: string) {
  return safeLinkPattern.test(url.trim());
}

export function richTextToPlainText(html: string | null | undefined) {
  return (html ?? '')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(?:blockquote|div|h[1-6]|li|ol|p|pre|ul)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(
      /&(?:amp|apos|gt|lt|nbsp|quot|#\d+|#x[\da-f]+);/gi,
      decodeHtmlEntity,
    )
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export function isRichTextEmpty(html: string | null | undefined) {
  return richTextToPlainText(html).length === 0;
}
